import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Shield, Flag, Eye, EyeOff, Trash2, ChevronUp, Check, Clock,
  AlertTriangle, BarChart3, RefreshCw, Filter, ImageOff,
} from 'lucide-react';

const REASON_LABELS = {
  spam_scam:          'Spam or scam',
  harassment_hate:    'Harassment or hate',
  nudity_sexual:      'Nudity or sexual content',
  violence_dangerous: 'Violence or dangerous content',
  illegal_prohibited: 'Illegal or prohibited content',
  other:              'Other',
};

const STATUS_COLORS = {
  pending:   'text-yellow-500 bg-yellow-500/10 border-yellow-500/20',
  dismissed: 'text-muted-foreground bg-muted border-border',
  actioned:  'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
  escalated: 'text-[hsl(var(--mg-purple))] bg-[hsl(var(--mg-purple)/0.1)] border-[hsl(var(--mg-purple)/0.3)]',
  reviewed:  'text-sky-500 bg-sky-500/10 border-sky-500/20',
};

function StatusChip({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[status] || STATUS_COLORS.reviewed}`}>
      {status}
    </span>
  );
}

function ActionDialog({ open, title, onConfirm, onCancel, loading }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => { if (open) { setReason(''); setNote(''); } }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4">
        <h3 className="font-heading font-bold text-sm">{title}</h3>
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground font-medium">Reason (required)</label>
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Brief reason for this action…"
            className="w-full h-9 rounded-md bg-muted/50 border border-transparent px-3 text-sm focus:border-primary focus:outline-none"
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground font-medium">Internal note (optional)</label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Additional context for the audit trail…"
            className="resize-none text-sm bg-muted/50 border-transparent focus:border-primary min-h-[70px]"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} className="rounded-full" disabled={loading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm(reason.trim(), note.trim())}
            disabled={!reason.trim() || loading}
            className="rounded-full bg-primary"
          >
            {loading ? 'Saving…' : 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReportCard({ report, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const preview = report.target_preview;
  const user = report.target_user;

  const doAction = useCallback(async (action, reason, note) => {
    setActionLoading(true);
    try {
      await onAction(report, action, reason, note);
      setActionDialog(null);
    } finally {
      setActionLoading(false);
    }
  }, [report, onAction]);

  const actions = report.status === 'pending' || report.status === 'escalated' ? [
    {
      key: 'dismiss',
      label: 'Dismiss',
      icon: Check,
      className: 'text-muted-foreground border-border hover:border-muted-foreground/50',
      title: 'Dismiss report',
    },
    {
      key: 'hide',
      label: 'Hide',
      icon: EyeOff,
      className: 'text-yellow-600 border-yellow-500/30 hover:border-yellow-500',
      title: `Hide this ${report.target_type}`,
    },
    {
      key: 'remove',
      label: 'Remove',
      icon: Trash2,
      className: 'text-destructive border-destructive/30 hover:border-destructive',
      title: `Mark ${report.target_type} as removed`,
    },
    {
      key: 'escalate',
      label: 'Escalate',
      icon: ChevronUp,
      className: 'text-[hsl(var(--mg-purple))] border-[hsl(var(--mg-purple)/0.4)] hover:border-[hsl(var(--mg-purple))]',
      title: 'Escalate to Admin',
    },
  ] : [
    {
      key: 'restore',
      label: 'Restore',
      icon: Eye,
      className: 'text-emerald-500 border-emerald-500/30 hover:border-emerald-500',
      title: `Restore ${report.target_type} to normal visibility`,
    },
  ];

  return (
    <div className="mg-card-elevated rounded-xl overflow-hidden" data-testid={`report-${report.id}`}>
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusChip status={report.status} />
            {report.source === 'auto' && (
              <span className="text-xs font-medium text-sky-500 bg-sky-500/10 px-1.5 py-0.5 rounded-full border border-sky-500/25 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />auto-text
              </span>
            )}
            {report.source === 'auto_media' && (
              <span className="text-xs font-medium text-orange-500 bg-orange-500/10 px-1.5 py-0.5 rounded-full border border-orange-500/25 inline-flex items-center gap-1">
                <ImageOff className="w-3 h-3" />auto-media
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {report.target_type} · {REASON_LABELS[report.reason] || report.reason}
            </span>
            {report.source === 'auto' && report.matched_rules?.length > 0 && (
              <span className="text-xs text-muted-foreground/70">
                [{report.matched_rules.join(', ')}]
              </span>
            )}
            {report.source === 'auto' && report.confidence && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium ${
                report.confidence === 'high'
                  ? 'text-red-500 bg-red-500/10 border-red-500/25'
                  : report.confidence === 'medium'
                  ? 'text-yellow-500 bg-yellow-500/10 border-yellow-500/25'
                  : 'text-muted-foreground bg-muted border-border'
              }`}>
                {report.confidence} confidence
              </span>
            )}
            {report.total_reports_on_target > 1 && (
              <span className="text-xs font-medium text-destructive bg-destructive/10 px-1.5 py-0.5 rounded-full border border-destructive/20">
                {report.total_reports_on_target} reports
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {new Date(report.created_at).toLocaleString()}
          </p>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <Filter className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Reported user */}
      {user && (
        <div className="px-4 pb-3 flex items-center gap-2">
          <Avatar className="w-7 h-7 shrink-0">
            <AvatarImage src={user.avatar} />
            <AvatarFallback className="text-xs">{user.display_name?.[0]}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium truncate">{user.display_name}</span>
          <span className="text-xs text-muted-foreground truncate">@{user.username}</span>
          {user.is_npc && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--mg-purple)/0.15)] text-[hsl(var(--mg-purple))] border border-[hsl(var(--mg-purple)/0.3)]">NPC</span>
          )}
        </div>
      )}

      {/* Content preview */}
      {preview && (
        <div className={`mx-4 mb-3 p-3 rounded-lg bg-muted/40 border border-border text-sm ${preview.moderation_state ? 'opacity-60' : ''}`}>
          {preview.content && (
            <p className="text-foreground line-clamp-3">{preview.content}</p>
          )}
          {preview.media_urls?.length > 0 && (
            <div className="mt-2 flex gap-2">
              {preview.media_urls.slice(0, 3).map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover border border-border"
                />
              ))}
            </div>
          )}
          {preview.moderation_state && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Current state: <span className="font-medium">{preview.moderation_state}</span>
            </p>
          )}
        </div>
      )}

      {/* Expanded: explanation / auto-detection evidence */}
      {expanded && report.explanation && (
        <div className="mx-4 mb-3 p-3 rounded-lg bg-muted/30 border border-border">
          <p className="text-xs text-muted-foreground font-medium mb-1">
            {report.source === 'auto' ? 'Text detection evidence'
              : report.source === 'auto_media' ? 'Media detection evidence'
              : "Reporter's explanation"}
          </p>
          <p className="text-sm">{report.explanation}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 px-4 pb-4">
        {actions.map(({ key, label, icon: Icon, className, title }) => (
          <button
            key={key}
            onClick={() => setActionDialog({ key, title })}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${className}`}
            title={title}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Action dialog */}
      <ActionDialog
        open={!!actionDialog}
        title={actionDialog?.title || ''}
        onConfirm={(reason, note) => doAction(actionDialog.key, reason, note)}
        onCancel={() => setActionDialog(null)}
        loading={actionLoading}
      />
    </div>
  );
}

export default function ModerationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [total, setTotal] = useState(0);

  const isAuthorized = user?.role === 'moderator' || user?.role === 'admin';

  useEffect(() => {
    if (!isAuthorized) { navigate('/home'); return; }
    fetchStats();
    fetchReports(statusFilter);
  }, [isAuthorized]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = async () => {
    try {
      const res = await api.get('/moderation/stats');
      setStats(res.data);
    } catch {}
  };

  const fetchReports = async (status) => {
    setLoading(true);
    try {
      const res = await api.get(`/moderation/reports?status=${status}&limit=50`);
      setReports(res.data.reports || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (s) => {
    setStatusFilter(s);
    fetchReports(s);
  };

  const handleAction = async (report, action, reason, note) => {
    try {
      const body = { reason, note: note || undefined };
      if (action === 'dismiss') {
        await api.post(`/moderation/reports/${report.id}/dismiss`, body);
        toast.success('Report dismissed');
      } else if (action === 'escalate') {
        await api.post(`/moderation/reports/${report.id}/escalate`, body);
        toast.success('Report escalated to Admin');
      } else if (action === 'hide') {
        await api.post(`/moderation/${report.target_type}s/${report.target_id}/hide`, body);
        toast.success(`${report.target_type === 'post' ? 'Post' : 'Comment'} hidden`);
      } else if (action === 'remove') {
        await api.post(`/moderation/${report.target_type}s/${report.target_id}/remove`, body);
        toast.success(`${report.target_type === 'post' ? 'Post' : 'Comment'} removed`);
      } else if (action === 'restore') {
        await api.post(`/moderation/${report.target_type}s/${report.target_id}/restore`, body);
        toast.success('Content restored');
      }
      await fetchReports(statusFilter);
      await fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
      throw err;
    }
  };

  if (!isAuthorized) return null;

  const STATUS_TABS = [
    { key: 'pending',   label: 'Pending' },
    { key: 'escalated', label: 'Escalated' },
    { key: 'actioned',  label: 'Actioned' },
    { key: 'dismissed', label: 'Dismissed' },
    { key: 'all',       label: 'All' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6 min-[900px]:ml-60" data-testid="moderation-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[hsl(var(--mg-purple)/0.15)] border border-[hsl(var(--mg-purple)/0.3)] flex items-center justify-center">
          <Shield className="w-5 h-5 text-[hsl(var(--mg-purple))]" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-xl">The Wardens' Hall</h1>
          <p className="text-sm text-muted-foreground">Content moderation and community safety</p>
        </div>
        <button
          onClick={() => { fetchStats(); fetchReports(statusFilter); }}
          className="ml-auto p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-yellow-500' },
            { label: 'Escalated', value: stats.escalated, icon: AlertTriangle, color: 'text-[hsl(var(--mg-purple))]' },
            { label: 'Actioned (24h)', value: stats.actioned_today, icon: BarChart3, color: 'text-emerald-500' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="mg-card-elevated rounded-xl p-3 flex flex-col gap-1">
              <Icon className={`w-4 h-4 ${color}`} />
              <p className="text-2xl font-bold tabular-nums">{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Status filter tabs */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-1">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleFilterChange(key)}
            className={`flex-none px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              statusFilter === key
                ? 'bg-[hsl(var(--mg-purple))] text-white'
                : 'bg-muted/50 text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Role badge */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Shield className="w-3.5 h-3.5" />
        <span>Signed in as <span className="font-semibold text-foreground">{user?.display_name}</span> · Role: <span className="font-semibold text-[hsl(var(--mg-purple))] capitalize">{user?.role}</span></span>
      </div>

      {/* Reports list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
          <RefreshCw className="w-5 h-5 animate-spin" />
          Loading reports…
        </div>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
          <Flag className="w-8 h-8 opacity-40" />
          <p>No {statusFilter === 'all' ? '' : statusFilter + ' '}reports</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">{total} report{total !== 1 ? 's' : ''}</p>
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} onAction={handleAction} />
          ))}
        </div>
      )}
    </div>
  );
}
